import { useState, useCallback, useEffect } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

interface WebhookStatus {
  isActive: boolean;
  url: string;
  lastError: string | null;
}

interface ChannelStatusResponse {
  channels: Array<{
    id: string;
    name: string;
    status: "active" | "error" | "not_configured";
    enabled: boolean;
  }>;
}

interface VerifyResponse {
  valid: boolean;
  error?: string;
  botInfo?: {
    name: string;
    username: string;
  };
}

interface WebhookResponse {
  configured: boolean;
  webhookUrl?: string;
  error?: string;
}

interface TestResponse {
  sent: boolean;
  error?: string;
}

const INPUT_CLASS =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

function extractError(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function StatusIndicator({ isActive, label }: { isActive: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-2.5 w-2.5 rounded-full ${
          isActive ? "bg-[var(--color-success)]" : "bg-muted-foreground"
        }`}
      />
      <span className="text-sm text-foreground">{label}</span>
    </div>
  );
}

export default function TelegramConfigPage() {
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<WebhookStatus | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isConfiguringWebhook, setIsConfiguringWebhook] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  useEffect(() => {
    apiFetch<ChannelStatusResponse>("/api/messaging/channels")
      .then((res) => {
        const telegramChannel = res.channels.find((channel) => channel.id === "telegram");
        if (!telegramChannel) {
          setWebhookStatus(null);
          return;
        }

        setWebhookStatus({
          isActive: telegramChannel.enabled && telegramChannel.status === "active",
          url: "",
          lastError: telegramChannel.status === "error" ? "Provider reported an error state." : null,
        });
      })
      .catch(() => setWebhookStatus(null))
      .finally(() => setIsLoadingStatus(false));
  }, []);

  const handleVerifyToken = useCallback(async () => {
    if (!botToken.trim()) return;
    setIsVerifying(true);
    setError(null);
    setVerificationResult(null);

    try {
      const result = await apiFetch<VerifyResponse>("/api/messaging/telegram/verify", {
        method: "POST",
        body: JSON.stringify({ token: botToken }),
      });

      if (!result.valid) {
        setVerificationResult({
          ok: false,
          message: result.error ?? "Bot token verification failed.",
        });
        return;
      }

      const botLabel = result.botInfo?.username
        ? `${result.botInfo.name} (@${result.botInfo.username})`
        : (result.botInfo?.name ?? "Bot token verified");

      setVerificationResult({
        ok: true,
        message: `Connected to ${botLabel}`,
      });
    } catch (err: unknown) {
      setVerificationResult({
        ok: false,
        message: extractError(err, "Bot token verification failed"),
      });
    } finally {
      setIsVerifying(false);
    }
  }, [botToken]);

  const handleConfigureWebhook = useCallback(async () => {
    if (!botToken.trim()) return;
    setIsConfiguringWebhook(true);
    setError(null);

    try {
      const result = await apiFetch<WebhookResponse>("/api/messaging/telegram/webhook", {
        method: "POST",
        body: JSON.stringify({ token: botToken }),
      });

      if (!result.configured) {
        setError(result.error ?? "Failed to configure webhook");
        return;
      }

      setWebhookStatus({
        isActive: true,
        url: result.webhookUrl ?? "",
        lastError: null,
      });
    } catch (err: unknown) {
      setError(extractError(err, "Failed to configure webhook"));
    } finally {
      setIsConfiguringWebhook(false);
    }
  }, [botToken]);

  const handleTestConnection = useCallback(async () => {
    if (!botToken.trim() || !chatId.trim()) return;
    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await apiFetch<TestResponse>("/api/messaging/telegram/test", {
        method: "POST",
        body: JSON.stringify({ token: botToken, chatId }),
      });

      if (!result.sent) {
        setTestResult({
          ok: false,
          message: result.error ?? "Connection test failed",
        });
        return;
      }

      setTestResult({ ok: true, message: "Test message sent successfully" });
    } catch (err: unknown) {
      setTestResult({ ok: false, message: extractError(err, "Connection test failed") });
    } finally {
      setIsTesting(false);
    }
  }, [botToken, chatId]);

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Telegram Bot Configuration</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your Telegram bot to verify credentials, configure the webhook, send test messages,
          and support inline action replies from the shared inbox.
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <div className="rounded-lg border border-border p-5 space-y-3">
        <h3 className="text-sm font-medium text-foreground">Bot Token</h3>
        <p className="text-xs text-muted-foreground">
          Get your bot token from @BotFather on Telegram.
        </p>
        <div className="relative">
          <input
            type={isTokenVisible ? "text" : "password"}
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
            className={INPUT_CLASS}
          />
          <button
            type="button"
            onClick={() => setIsTokenVisible((visible) => !visible)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
          >
            {isTokenVisible ? "Hide" : "Show"}
          </button>
        </div>
        <button
          onClick={handleVerifyToken}
          disabled={!botToken.trim() || isVerifying}
          className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
        >
          {isVerifying ? "Verifying..." : "Verify Bot Token"}
        </button>
        {verificationResult ? (
          <div
            className={`rounded-md border p-3 text-sm ${
              verificationResult.ok
                ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                : "border-destructive/30 bg-destructive/10 text-destructive"
            }`}
          >
            {verificationResult.message}
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border border-border p-5 space-y-3">
        <h3 className="text-sm font-medium text-foreground">Webhook</h3>
        <button
          onClick={handleConfigureWebhook}
          disabled={!botToken.trim() || isConfiguringWebhook}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isConfiguringWebhook ? "Configuring..." : "Auto-configure Webhook"}
        </button>

        {isLoadingStatus ? (
          <p className="text-sm text-muted-foreground">Loading channel status...</p>
        ) : webhookStatus ? (
          <div className="space-y-2">
            <StatusIndicator
              isActive={webhookStatus.isActive}
              label={webhookStatus.isActive ? "Webhook active" : "Webhook inactive"}
            />
            {webhookStatus.url ? (
              <p className="break-all rounded-md bg-muted px-3 py-2 font-mono text-xs text-muted-foreground">
                {webhookStatus.url}
              </p>
            ) : null}
            {webhookStatus.lastError ? (
              <p className="text-xs text-destructive">Last error: {webhookStatus.lastError}</p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Channel status is currently unavailable. You can still verify credentials and configure the webhook directly.
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border p-5 space-y-3">
        <h3 className="text-sm font-medium text-foreground">Test Message</h3>
        <p className="text-xs text-muted-foreground">
          Send a real Telegram test message after your bot token has been verified and the webhook is configured.
        </p>
        <input
          type="text"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="Telegram chat ID"
          className={INPUT_CLASS}
        />
        <button
          onClick={handleTestConnection}
          disabled={!botToken.trim() || !chatId.trim() || isTesting}
          className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
        >
          {isTesting ? "Testing..." : "Send Test Message"}
        </button>
        {testResult ? (
          <div
            className={`rounded-md border p-3 text-sm ${
              testResult.ok
                ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                : "border-destructive/30 bg-destructive/10 text-destructive"
            }`}
          >
            {testResult.message}
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border border-border p-5 space-y-3">
        <h3 className="text-sm font-medium text-foreground">Inline actions</h3>
        <p className="text-xs text-muted-foreground">
          Mounted now: Telegram replies from the inbox can attach Bot API inline keyboards, and
          callback_query updates are captured back into the same Telegram conversation as action messages.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="text-xs font-medium text-foreground">Book call</p>
            <p className="mt-1 text-xs text-muted-foreground">Buttons: Book a call, Ask a question</p>
          </div>
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="text-xs font-medium text-foreground">Show options</p>
            <p className="mt-1 text-xs text-muted-foreground">Buttons: Pricing, Support</p>
          </div>
        </div>
      </div>
    </div>
  );
}
